import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  isLocalDialogList,
  pinnedChatIdsForList,
  useRealtimeStore
} from "../../lib/store.js";
import { QUICK_ACTION_CATALOG, useUiPrefs, type QuickActionId } from "../../lib/ui-prefs.js";
import { pushToast } from "../../lib/toast.js";
import { useTrustTemplates } from "../chat/use-trust-templates.js";

type PageId = "root" | "folders" | "tags" | "templates" | "pin";

interface PaletteItem {
  id: string;
  title: string;
  icon: string;
  shortcut?: string;
  submenu?: PageId;
  run?: () => void;
  /** Пункт-навигация внутри страницы (раздел шаблонов): палитру не закрывать. */
  keepOpen?: boolean;
}

// Готовый набор тегов для быстрой простановки из палитры.
const TAG_CHOICES = ["VIP", "Клиент", "Инвестиции", "Важно", "Новый", "Депозит", "Проблемный"];

const norm = (value: string): string => value.trim().toLowerCase();

// Номер пункта для горячей клавиши: 1…9, десятый — 0 (как на клавиатуре).
const hotkeyLabel = (index: number): string | null =>
  index < 9 ? String(index + 1) : index === 9 ? "0" : null;

const indexForDigit = (digit: string): number => (digit === "0" ? 9 : Number(digit) - 1);

const isDigitKey = (key: string): boolean => key.length === 1 && key >= "0" && key <= "9";

const listTitle = (list: { title: string; kind: string }): string =>
  list.kind === "main" ? "Общие" : list.title;

// Событие «открыть палитру» — CommandPalette его слушает и переключается
// в open. Используется кнопкой в шапке и любыми будущими вызовами (пункт
// меню и т.п.). Отдельная точка входа нужна, чтобы у саппортов с
// заблокированным Ctrl+M/Ctrl+; всё равно был доступ к палитре.
const OPEN_EVENT = "tg:open-command-palette";

export const openCommandPalette = (): void => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OPEN_EVENT));
  }
};

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<PageId>("root");
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  // Раздел на странице «Отправить шаблон»: null — корень (разделы верхнего
  // уровня), иначе id раздела/подраздела. Навигация как в панели шаблонов.
  const [tplSectionId, setTplSectionId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const selectedChatId = useRealtimeStore((s) => s.selectedChatId);
  const chat = useRealtimeStore((s) =>
    s.selectedChatId ? s.chats[s.selectedChatId] ?? null : null
  );
  const translated = useRealtimeStore((s) =>
    s.selectedChatId ? Boolean(s.translatedChats[s.selectedChatId]) : false
  );
  const dialogLists = useRealtimeStore((s) => s.dialogLists);
  const activeDialogListId = useRealtimeStore((s) => s.activeDialogListId);
  const setChatTranslation = useRealtimeStore((s) => s.setChatTranslation);
  const placeCall = useRealtimeStore((s) => s.placeCall);
  const setListMembership = useRealtimeStore((s) => s.setListMembership);
  const updateChatFolder = useRealtimeStore((s) => s.updateChatFolder);
  const setChatInternalTags = useRealtimeStore((s) => s.setChatInternalTags);
  const toggleArchived = useRealtimeStore((s) => s.toggleArchived);
  const togglePinned = useRealtimeStore((s) => s.togglePinned);

  const setRightPanelTab = useUiPrefs((s) => s.setRightPanelTab);
  const openPushLaunch = useUiPrefs((s) => s.openPushLaunch);
  const quickActionIds = useUiPrefs((s) => s.quickActionIds);

  // Реальные шаблоны аккаунта (раньше здесь лежал демо-список из mock-режима).
  // Грузим только когда открыта страница «Отправить шаблон».
  const trust = useTrustTemplates(open && page === "templates");

  const close = useCallback(() => {
    setOpen(false);
    setPage("root");
    setQuery("");
    setActiveIndex(0);
    setTplSectionId(null);
  }, []);

  // Глобальный ⌘M / Ctrl+M — открыть/закрыть палитру. Раньше висело на ⌘K,
  // но у большинства саппортов Ctrl+K в браузере — «поиск в адресной строке»
  // (Chrome/Chromium-based, включая Dolphin), фокус уходил из клиента.
  // Сравниваем по event.code, а не по event.key: на русской раскладке
  // физическая M отдаёт event.key === "ь", и Ctrl+M не срабатывал у
  // саппортов на Windows. event.code === "KeyM" стабилен между раскладками.
  //
  // Дублирующий ⌘/Ctrl + ; для тех, у кого Ctrl+M занят браузером
  // (Яндекс.Браузер / корпоративные policy / IME). Плюс кнопка в шапке
  // и событие openCommandPalette() — палитра остаётся доступной, даже
  // если ОБА шортката браузер перехватывает.
  useEffect(() => {
    const toggle = (): void => {
      setOpen((value) => {
        const next = !value;
        if (!next) {
          setPage("root");
          setQuery("");
          setActiveIndex(0);
        }
        return next;
      });
    };
    const onKey = (event: KeyboardEvent): void => {
      if (
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        !event.altKey &&
        (event.code === "KeyM" || event.code === "Semicolon")
      ) {
        event.preventDefault();
        toggle();
      }
    };
    const onOpenEvent = (): void => {
      setOpen((value) => {
        if (value) return value;
        setPage("root");
        setQuery("");
        setActiveIndex(0);
        return true;
      });
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_EVENT, onOpenEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_EVENT, onOpenEvent);
    };
  }, []);

  useEffect(() => {
    if (open) {
      // Фокус на поле после появления.
      window.requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open, page]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, page, tplSectionId]);

  const folderItems = useMemo<PaletteItem[]>(
    () =>
      Object.values(dialogLists)
        .filter((list) => list.kind === "folder" || list.kind === "local")
        .map((list) => ({
          id: `folder:${list.id}`,
          title: list.title,
          icon: "ic-folder",
          run: () => {
            if (!selectedChatId || !chat) return;
            if (list.kind === "local") {
              // Локальная папка живёт в БД gateway, воркер про неё не знает:
              // chat.setListMembership с local:* молча ничего не делал, а
              // тост обещал «Перемещено».
              void updateChatFolder(list.id, { addChatIds: [selectedChatId] });
              pushToast(`Добавлено в «${list.title}»`);
              return;
            }
            const next = Array.from(new Set([...chat.listIds, list.id]));
            setListMembership(selectedChatId, next);
            pushToast(`Добавлено в «${list.title}»`);
          }
        })),
    [dialogLists, selectedChatId, chat, setListMembership, updateChatFolder]
  );

  // Закрепить/открепить в одной из папок, где чат уже лежит («Общие»,
  // папки Telegram, локальные папки). Архив не предлагаем.
  const pinItems = useMemo<PaletteItem[]>(() => {
    if (!selectedChatId || !chat) return [];
    const state = useRealtimeStore.getState();
    const ids = chat.listIds.includes("main") ? chat.listIds : ["main", ...chat.listIds];
    return ids
      .map((listId) => dialogLists[listId])
      .filter(
        (list): list is NonNullable<typeof list> =>
          Boolean(list) && list!.kind !== "archive"
      )
      .map((list) => {
        const pinned = isLocalDialogList(list.id)
          ? pinnedChatIdsForList(state, list.id).includes(selectedChatId)
          : (chat.pinnedListIds?.includes(list.id) ?? false);
        const title = listTitle(list);
        return {
          id: `pin:${list.id}`,
          title: pinned ? `Открепить в «${title}»` : `Закрепить в «${title}»`,
          icon: "ic-pin",
          run: () => {
            togglePinned(selectedChatId, !pinned, list.id);
            pushToast(pinned ? `Откреплено в «${title}»` : `Закреплено в «${title}»`);
          }
        };
      });
  }, [selectedChatId, chat, dialogLists, togglePinned]);

  const tagItems = useMemo<PaletteItem[]>(
    () =>
      TAG_CHOICES.map((tag) => {
        const active = Boolean(chat?.internalTags.includes(tag));
        return {
          id: `tag:${tag}`,
          // Тоггл: если тег уже стоит — снять, иначе поставить.
          title: active ? `${tag} — снять` : tag,
          icon: "ic-pin",
          run: () => {
            if (!selectedChatId || !chat) return;
            if (active) {
              setChatInternalTags(
                selectedChatId,
                chat.internalTags.filter((t) => t !== tag)
              );
              pushToast(`Тег «${tag}» снят`);
            } else {
              setChatInternalTags(
                selectedChatId,
                Array.from(new Set([...chat.internalTags, tag]))
              );
              pushToast(`Тег «${tag}» добавлен`);
            }
          }
        };
      }),
    [selectedChatId, chat, setChatInternalTags]
  );

  const templateItem = useCallback(
    (asset: (typeof trust.assets)[number]): PaletteItem => ({
      id: `tpl:${asset.assetId}`,
      title: asset.title,
      icon: "ic-copy",
      run: () => {
        if (!selectedChatId) return;
        trust.send(asset);
        pushToast(`Шаблон «${asset.title}» отправлен`);
      }
    }),
    [trust, selectedChatId]
  );

  const sectionIds = useMemo(
    () => new Set(trust.sections.map((section) => section.sectionId)),
    [trust.sections]
  );

  // Страница «Отправить шаблон»: та же иерархия, что в панели — разделы,
  // подразделы, внутри шаблоны. При поиске — плоский список по всем шаблонам.
  const templateItems = useMemo<PaletteItem[]>(() => {
    const byTitle = (left: { title: string }, right: { title: string }) =>
      left.title.localeCompare(right.title, "ru");
    const assetsSorted = [...trust.assets].sort(byTitle);
    if (norm(query).length > 0 || trust.sections.length === 0) {
      return assetsSorted.map(templateItem);
    }
    const childSections = trust.sections
      .filter((section) => section.parentId === tplSectionId)
      .sort(byTitle);
    const countIn = (sectionId: string): number => {
      const nested = new Set<string>([sectionId]);
      for (const section of trust.sections) {
        if (section.parentId === sectionId) nested.add(section.sectionId);
      }
      return trust.assets.filter((asset) => asset.sectionId && nested.has(asset.sectionId)).length;
    };
    const items: PaletteItem[] = childSections.map((section) => ({
      id: `tpl-section:${section.sectionId}`,
      title: `${section.title} · ${countIn(section.sectionId)}`,
      icon: "ic-folder",
      keepOpen: true,
      run: () => {
        setTplSectionId(section.sectionId);
        setQuery("");
        setActiveIndex(0);
      }
    }));
    const inSection = assetsSorted.filter((asset) =>
      tplSectionId === null
        ? !asset.sectionId || !sectionIds.has(asset.sectionId)
        : asset.sectionId === tplSectionId
    );
    if (tplSectionId === null && inSection.length > 0) {
      // Шаблоны без раздела — отдельным пунктом, как «Без раздела» в панели.
      items.push({
        id: "tpl-section:unsorted",
        title: `Без раздела · ${inSection.length}`,
        icon: "ic-folder",
        keepOpen: true,
        run: () => {
          setTplSectionId("unsorted");
          setQuery("");
          setActiveIndex(0);
        }
      });
      return items;
    }
    if (tplSectionId === "unsorted") {
      return assetsSorted
        .filter((asset) => !asset.sectionId || !sectionIds.has(asset.sectionId))
        .map(templateItem);
    }
    return [...items, ...inSection.map(templateItem)];
  }, [trust.assets, trust.sections, sectionIds, tplSectionId, query, templateItem]);

  // Подняться на уровень вверх внутри «Отправить шаблон»; false — уже корень.
  const templatesGoUp = useCallback((): boolean => {
    if (page !== "templates" || tplSectionId === null) return false;
    const current =
      tplSectionId === "unsorted"
        ? null
        : trust.sections.find((section) => section.sectionId === tplSectionId) ?? null;
    setTplSectionId(current?.parentId ?? null);
    setQuery("");
    setActiveIndex(0);
    return true;
  }, [page, tplSectionId, trust.sections]);

  const isArchived = Boolean(chat?.listIds.includes("archive"));

  const rootItems = useMemo<PaletteItem[]>(() => {
    const hasChat = Boolean(selectedChatId && chat);
    // ИИ-пропуш действует на активную папку, чат для него не нужен — поэтому
    // root больше не блокируется целиком при !hasChat. Пункты, которым чат
    // ОБЯЗАТЕЛЕН, отсеиваются точечно ниже.
    const activeList = dialogLists[activeDialogListId] ?? null;
    const listIsFolder =
      Boolean(activeList) &&
      (activeList!.kind === "folder" || activeList!.kind === "local");
    const byId: Record<QuickActionId, PaletteItem | null> = {
      translate: hasChat
        ? {
            id: "translate",
            title: translated ? "Показать оригинал чата" : "Перевести весь чат",
            icon: "ic-translate",
            run: () => {
              if (!selectedChatId) return;
              setChatTranslation(selectedChatId, !translated);
              pushToast(translated ? "Показан оригинал" : "Перевод чата включён");
            }
          }
        : null,
      tag: hasChat ? { id: "tag", title: "Поставить тег", icon: "ic-pin", submenu: "tags" } : null,
      move: hasChat
        ? {
            id: "move",
            title: "Переместить пользователя в папку",
            icon: "ic-folder",
            submenu: "folders"
          }
        : null,
      template: hasChat
        ? { id: "template", title: "Отправить шаблон", icon: "ic-copy", submenu: "templates" }
        : null,
      payments: hasChat
        ? {
            id: "payments",
            title: "Открыть способы оплаты",
            icon: "ic-card",
            run: () => setRightPanelTab("payments")
          }
        : null,
      schedule: hasChat
        ? {
            id: "schedule",
            title: "Запланировать сообщение",
            icon: "ic-schedule",
            run: () => setRightPanelTab("scheduled")
          }
        : null,
      call: hasChat
        ? {
            id: "call",
            title: "Позвонить",
            icon: "ic-phone",
            run: () => {
              if (!selectedChatId) return;
              placeCall(selectedChatId);
              pushToast("Звоним…", "info");
            }
          }
        : null,
      info: hasChat
        ? {
            id: "info",
            title: "Открыть Info пользователя",
            icon: "ic-info",
            run: () => setRightPanelTab("info")
          }
        : null,
      archive: hasChat
        ? {
            id: "archive",
            title: isArchived ? "Вернуть пользователя из архива" : "Отправить пользователя в архив",
            icon: "ic-archive-filled",
            run: () => {
              if (!selectedChatId) return;
              toggleArchived(selectedChatId, !isArchived);
              pushToast(isArchived ? "Возвращено из архива" : "Отправлено в архив");
            }
          }
        : null,
      pin: hasChat
        ? {
            id: "pin",
            title: "Закрепить пользователя в папке",
            icon: "ic-pin",
            submenu: "pin"
          }
        : null,
      "ai-folder-push": {
        id: "ai-folder-push",
        // В подписи — реальное имя активной папки, чтобы саппорт видел, ПО ЧЕМУ
        // сейчас пойдёт пуш (иначе легко перепутать вкладку).
        title:
          listIsFolder && activeList
            ? `ИИ-пропуш по папке «${activeList.title}»`
            : "ИИ-пропуш по папке",
        icon: "ic-send",
        run: () => {
          if (!listIsFolder || !activeList) {
            pushToast("Выберите папку — пуш недоступен на «Общих» и в архиве", "info");
            return;
          }
          openPushLaunch(activeList.id, activeList.title);
        }
      }
    };
    // Порядок — каталожный; выключенные в настройках пункты не показываем,
    // номера считаются по оставшимся видимым.
    return QUICK_ACTION_CATALOG.filter((action) => quickActionIds.includes(action.id))
      .map((action) => byId[action.id])
      .filter((item): item is PaletteItem => item !== null);
  }, [
    selectedChatId,
    chat,
    translated,
    isArchived,
    quickActionIds,
    dialogLists,
    activeDialogListId,
    setChatTranslation,
    setRightPanelTab,
    placeCall,
    toggleArchived,
    openPushLaunch
  ]);

  const pageItems =
    page === "folders"
      ? folderItems
      : page === "tags"
        ? tagItems
        : page === "templates"
          ? templateItems
          : page === "pin"
            ? pinItems
            : rootItems;

  const filtered = useMemo(() => {
    const q = norm(query);
    if (!q) return pageItems;
    return pageItems.filter((item) => norm(item.title).includes(q));
  }, [pageItems, query]);

  const runItem = useCallback(
    (item: PaletteItem) => {
      if (item.submenu) {
        setPage(item.submenu);
        setTplSectionId(null);
        setQuery("");
        setActiveIndex(0);
        return;
      }
      item.run?.();
      if (!item.keepOpen) {
        close();
      }
    },
    [close]
  );

  const onInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
      } else if (event.key === "Enter") {
        event.preventDefault();
        const item = filtered[activeIndex];
        if (item) runItem(item);
      } else if (event.key === "Escape") {
        event.preventDefault();
        if (templatesGoUp()) {
          return;
        }
        if (page !== "root") {
          setPage("root");
          setQuery("");
          setActiveIndex(0);
        } else {
          close();
        }
      } else if (event.key === "Backspace" && query.length === 0 && page !== "root") {
        event.preventDefault();
        if (templatesGoUp()) {
          return;
        }
        setPage("root");
      } else if (
        isDigitKey(event.key) &&
        query.length === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        // Цифра при пустом поиске — сразу пункт с этим номером. Если поле не
        // пустое, цифра — обычный символ запроса.
        const item = filtered[indexForDigit(event.key)];
        if (item) {
          event.preventDefault();
          runItem(item);
        }
      }
    },
    [filtered, activeIndex, runItem, page, query, close, templatesGoUp]
  );

  if (!open) return null;

  const placeholder =
    page === "folders"
      ? "Выберите папку…"
      : page === "tags"
        ? "Выберите тег…"
        : page === "templates"
          ? trust.isLoading && trust.assets.length === 0
            ? "Загружаем шаблоны…"
            : tplSectionId === "unsorted"
              ? "Без раздела — выберите шаблон…"
              : tplSectionId
                ? `${trust.sections.find((section) => section.sectionId === tplSectionId)?.title ?? "Раздел"} — выберите…`
                : trust.sections.length > 0
                  ? "Выберите раздел или введите название шаблона…"
                  : "Выберите шаблон…"
          : page === "pin"
            ? "Где закрепить…"
            : "Поиск действия…";

  const emptyText =
    page === "templates"
      ? trust.isLoading
        ? "Загружаем шаблоны…"
        : trust.error ?? "Шаблонов пока нет"
      : page === "root" && !selectedChatId
        ? "Откройте чат — действия применяются к нему"
        : "Ничего не найдено";

  return (
    <div className="cmdk" role="dialog" aria-modal="true" aria-label="Быстрые действия">
      <div className="cmdk__backdrop" onClick={close} role="presentation" />
      <div className="cmdk__panel">
        <div className="cmdk__search">
          {page !== "root" ? (
            <button
              type="button"
              className="cmdk__back"
              onClick={() => {
                if (templatesGoUp()) return;
                setPage("root");
                setQuery("");
              }}
              aria-label="Назад"
            >
              <i className="ic ic-back" aria-hidden="true" />
            </button>
          ) : (
            <i className="ic ic-search cmdk__search-icon" aria-hidden="true" />
          )}
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={placeholder}
            aria-label={placeholder}
          />
          <kbd className="cmdk__hint">esc</kbd>
        </div>
        <div className="cmdk__list" role="listbox">
          {filtered.length === 0 ? (
            <div className="cmdk__empty">{emptyText}</div>
          ) : (
            filtered.map((item, index) => {
              const hotkey = query.length === 0 ? hotkeyLabel(index) : null;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={
                    index === activeIndex ? "cmdk__item cmdk__item--active" : "cmdk__item"
                  }
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => runItem(item)}
                >
                  {hotkey ? (
                    <kbd className="cmdk__item-num" aria-label={`Клавиша ${hotkey}`}>
                      {hotkey}
                    </kbd>
                  ) : (
                    <span className="cmdk__item-num cmdk__item-num--blank" aria-hidden="true" />
                  )}
                  <span className="cmdk__item-icon">
                    <i className={`ic ${item.icon}`} aria-hidden="true" />
                  </span>
                  <span className="cmdk__item-title">{item.title}</span>
                  {item.submenu || item.keepOpen ? (
                    <i className="ic ic-back cmdk__item-chevron" aria-hidden="true" />
                  ) : item.shortcut ? (
                    <kbd className="cmdk__item-shortcut">{item.shortcut}</kbd>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
